import { useTranslation } from 'react-i18next';

import { motion } from 'framer-motion';

import { Box, Button, styled, Typography } from '@mui/material';

import mapBg1 from 'Assets/images/map-bg-1.png';
import treneo2 from 'Assets/images/treneo-2.svg';
import { LoginState, useSelectLoginState } from 'Auth/data';
import { CloseIconButton } from 'Common/CloseIconButton';
import { AvatarConsts } from 'Common/UserAvatar/Avatar/Avatar.consts';
import { Card as BaseCard, Link, Logo } from 'Components';
import { connectToTrainerDesktop, useSelectTestCourse } from 'Courses/data';
import { urls } from 'Router';
import { useAppDispatch } from 'Store';
import { Colors } from 'Themes/colors';
import { Spacing } from 'Themes/spacing';
import {
  saveIsStartBannerHidden,
  useSelectCurrentUserInfo,
  useSelectUserDisplayName
} from 'User/data';
import type { UserDisplayModel } from 'User/userDisplayModel';
import { useBoolean } from 'Utils/customHooks';

import { BasicFullscreenViewer } from '../../../components/fullscreen-desktop-viewer/basic-fullscreen-viewer';
import { CourseCard } from '../../../components/shared';

const styledIconButtonSx = {
  position: 'absolute',
  cursor: 'pointer',
  top: '15px',
  right: '15px',
  color: Colors.darkGrey,
  zIndex: 1
};

const testCourseCloseButtonSx = {
  position: 'absolute',
  cursor: 'pointer',
  top: 0,
  right: 0,
  color: Colors.darkGrey,
  zIndex: 2
};

const Container = styled(Box)`
  position: relative;
  margin-bottom: 1rem;
`;

const SubContainer = styled(Box)`
  width: 100%;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Column = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const LeftColumn = styled(Column)`
  align-self: stretch;
`;

const TitleContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const WelcomeMessage = styled(Typography)`
  font-size: 30px !important;
  font-weight: 700 !important;
  color: ${Colors.black} !important;
  max-width: 370px;
`;

const ActionPanel = styled(Box)`
  display: flex;
  align-items: center;
  gap: 25px;
  min-height: 50px;
`;

const Img = styled('img')`
  background: url(${mapBg1}) no-repeat;
  max-height: 300px;
`;

const TestCourseWrapper = styled(motion.div)`
  position: relative;
  width: 100%;
  margin-top: ${Spacing.medium};
`;

type Props = {
  isHighlighted: boolean;
};

export const StartBanner = ({ isHighlighted }: Readonly<Props>) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const Card = styled(BaseCard)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: column;
  `;

  const currentUserInfo = useSelectCurrentUserInfo();
  const currentUserInfoDisplayName = useSelectUserDisplayName() ?? AvatarConsts.fallbackUserName;

  const currentUserModel = {
    displayName: currentUserInfoDisplayName,
    avatarColor: currentUserInfo.avatarColor,
    avatar: currentUserInfo.avatar
  } as UserDisplayModel;

  const testCourse = useSelectTestCourse();
  const loginState = useSelectLoginState();
  const isLogged = loginState === LoginState.LOGGED;

  const [shouldTestCourseDisplay, displayTestCourse, hideTestCourse] = useBoolean();

  const handleTestBannerClick = () => {
    displayTestCourse();

    if (testCourse?.id) {
      dispatch(connectToTrainerDesktop(testCourse.id));
    }
  };

  const onClose = () => {
    dispatch(saveIsStartBannerHidden({ isStartBannerHidden: true, isLogged }));
  };

  return (
    <Container>
      <Card>
        <SubContainer>
          <LeftColumn>
            <TitleContainer>
              <Typography>{t('startBanner.welcomeIn')}</Typography>
              <Logo variant="login" />
            </TitleContainer>

            <WelcomeMessage>{t('startBanner.welcomeMessage')}</WelcomeMessage>

            <ActionPanel>
              {!shouldTestCourseDisplay ? (
                <>
                  <Button
                    variant={isHighlighted ? 'contained' : 'outlined'}
                    onClick={handleTestBannerClick}
                  >
                    {t('startBanner.test')}
                  </Button>
                  <Typography>{t('startBanner.orSee')}</Typography>
                </>
              ) : (
                ''
              )}
              <Link to={urls.treneoSite} label={t('startBanner.whatIsTreneo')} />
            </ActionPanel>
          </LeftColumn>

          <Column>
            <Img width="100%" src={treneo2} />
          </Column>
        </SubContainer>

        {shouldTestCourseDisplay && testCourse ? (
          <TestCourseWrapper
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            <CourseCard key={testCourse.id} course={testCourse} user={currentUserModel} />
            <CloseIconButton onClose={hideTestCourse} sx={testCourseCloseButtonSx} />
          </TestCourseWrapper>
        ) : (
          ''
        )}

        <BasicFullscreenViewer />
      </Card>

      <CloseIconButton onClose={onClose} sx={styledIconButtonSx} />
    </Container>
  );
};
